import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { apiCallable } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { X, Loader2, User, Scissors, Sparkles } from 'lucide-react'
import { formatRelativeTime } from '@/lib/dateTime'
import { cn } from '@/lib/utils'
import { DetectionShot, type Detection, type ShotView } from '@/components/DetectionShot'

interface SplitPerson {
  id: string
  name: string | null
  classificationName: string | null
  totalSightings: number
  firstSeenAt: string | null
  lastSeenAt: string | null
  thumbnailUrl: string | null
}

interface DetectionsResponse {
  success: boolean
  person: SplitPerson
  detections: Detection[]
  nextCursor: string | null
}

interface DetectionScore {
  eventId: string
  similarity: number | null
}

interface SplitPersonDrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  groupId: string
  personId: string | null
  /** Called after a successful split so the caller can refresh its list. */
  onSplit: (result: { newPersonId: string; newPersonName: string; detectionsMoved: number }) => void
}

/**
 * Face similarity above which "select similar" pre-selects a detection. Sits in
 * the same band as the ingest auto-merge thresholds; it only ever *proposes* a
 * selection, which the reviewer confirms against the images before moving.
 */
const SIMILAR_THRESHOLD = 0.45

export function SplitPersonDrawer({
  open,
  onOpenChange,
  groupId,
  personId,
  onSplit,
}: SplitPersonDrawerProps) {
  const { t } = useTranslation()
  const [person, setPerson] = useState<SplitPerson | null>(null)
  const [detections, setDetections] = useState<Detection[]>([])
  const [cursor, setCursor] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [scoring, setScoring] = useState(false)
  const [splitting, setSplitting] = useState(false)
  const [confirming, setConfirming] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [view, setView] = useState<ShotView>('person')
  // Keyed by eventId — one entry per detection. Never by faceId: several
  // detections share a faceId, so that would move (and score) them as a block,
  // which is exactly the mis-grouping a split is here to undo.
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [scores, setScores] = useState<Map<string, number>>(new Map())

  const reset = () => {
    setPerson(null)
    setDetections([])
    setCursor(null)
    setSelected(new Set())
    setScores(new Map())
    setError(null)
    setNotice(null)
    setConfirming(false)
  }

  const load = useCallback(async (nextCursor?: string) => {
    if (!groupId || !personId) return
    if (nextCursor) setLoadingMore(true)
    else setLoading(true)
    setError(null)
    try {
      const fn = apiCallable('listPersonDetections')
      const res = await fn({ groupId, personId, cursor: nextCursor })
      const data = res.data as DetectionsResponse
      setPerson(data.person)
      setDetections((prev) => (nextCursor ? [...prev, ...data.detections] : data.detections))
      setCursor(data.nextCursor)
    } catch (e) {
      console.error('Failed to load person detections:', e)
      setError((e as Error)?.message || t('split.loadError'))
    } finally {
      setLoading(false)
      setLoadingMore(false)
    }
  }, [groupId, personId, t])

  useEffect(() => {
    if (!open) {
      reset()
      return
    }
    load()
  }, [open, load])

  const toggle = (eventId: string) => {
    setConfirming(false)
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(eventId)) next.delete(eventId)
      else next.add(eventId)
      return next
    })
  }

  const selectSimilar = async () => {
    if (!personId || selected.size === 0) return
    setScoring(true)
    setError(null)
    setNotice(null)
    try {
      const fn = apiCallable('scoreSimilarDetections')
      const res = await fn({ groupId, personId, seedEventIds: [...selected] })
      const data = res.data as { scores: DetectionScore[]; scanTruncated?: boolean }
      const byEvent = new Map<string, number>()
      for (const s of data.scores || []) {
        if (s.similarity != null) byEvent.set(s.eventId, s.similarity)
      }
      setScores(byEvent)

      // Scores cover the whole person, not just the loaded page — a match
      // further back stays selected and moves with the rest.
      const next = new Set(selected)
      for (const [eventId, similarity] of byEvent) {
        if (similarity >= SIMILAR_THRESHOLD) next.add(eventId)
      }
      const added = next.size - selected.size
      setSelected(next)
      setNotice(t('split.scoredNotice', { scored: byEvent.size, added }))
      if (data.scanTruncated) console.warn('scoreSimilarDetections: older detections were not scored')
    } catch (e) {
      console.error('Failed to score similar detections:', e)
      setError((e as Error)?.message || t('split.scoreError'))
    } finally {
      setScoring(false)
    }
  }

  const doSplit = async () => {
    if (!personId || selected.size === 0) return
    setSplitting(true)
    setError(null)
    try {
      const fn = apiCallable('splitPerson')
      const res = await fn({ groupId, personId, eventIds: [...selected] })
      const data = res.data as {
        newPersonId: string
        newPersonName: string
        detectionsMoved: number
        sightingsSkipped: number
      }
      if (data.sightingsSkipped > 0) {
        console.warn(`splitPerson: ${data.sightingsSkipped} sighting(s) could not be rewritten`)
      }
      onSplit(data)
      onOpenChange(false)
    } catch (e) {
      console.error('Failed to split person:', e)
      setError((e as Error)?.message || t('split.splitError'))
      setConfirming(false)
    } finally {
      setSplitting(false)
    }
  }

  // Only meaningful once every page is loaded — the backend is the real guard
  // (it refuses a split that would empty the person).
  const selectableCount = detections.filter((d) => d.eventId).length
  const allSelected = !cursor && selectableCount > 0 && selected.size >= selectableCount

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        {/* z sits above the merge-compare panel (z-50/60): this drawer can open
            stacked on top of it, and must never render behind it. */}
        <DialogPrimitive.Overlay
          className={cn(
            'fixed inset-0 z-[70] bg-ink/55 backdrop-blur-[4px]',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0'
          )}
        />
        <DialogPrimitive.Content
          className={cn(
            'fixed z-[80] flex flex-col bg-[rgba(246,241,230,0.98)] shadow-auvers-modal',
            // Mobile: bottom sheet
            'inset-x-0 bottom-0 max-h-[88vh] rounded-t-2xl border-t border-white/70',
            'pb-[env(safe-area-inset-bottom)]',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom',
            // Desktop ≥sm: right side panel, full height
            'sm:inset-y-0 sm:right-0 sm:bottom-auto sm:left-auto sm:h-full sm:w-[560px] sm:max-h-none',
            'lg:w-[680px]',
            'sm:rounded-none sm:rounded-l-2xl sm:border-l sm:border-t-0',
            'sm:data-[state=closed]:slide-out-to-right sm:data-[state=open]:slide-in-from-right'
          )}
        >
          {/* Header */}
          <div className="flex items-start justify-between gap-3 border-b border-rule px-5 py-4">
            <div className="min-w-0">
              <DialogPrimitive.Title className="font-serif text-[20px] font-medium leading-tight text-ink">
                {t('split.title')}
              </DialogPrimitive.Title>
              <DialogPrimitive.Description className="mt-1 text-[12px] text-ink-soft">
                {t('split.subtitle')}
              </DialogPrimitive.Description>
            </div>
            <DialogPrimitive.Close
              className="grid h-9 w-9 shrink-0 place-items-center rounded-md border border-ink/15 text-ink transition hover:bg-ink/5"
              aria-label={t('common.close')}
            >
              <X className="h-4 w-4" />
            </DialogPrimitive.Close>
          </div>

          {/* Person + controls */}
          {person && (
            <div className="border-b border-rule px-5 py-3">
              <div className="flex items-center gap-2">
                <div className="grid h-10 w-10 shrink-0 place-items-center overflow-hidden rounded-lg border border-rule bg-ink/5">
                  {person.thumbnailUrl ? (
                    <img src={person.thumbnailUrl} alt="" className="h-full w-full object-cover" />
                  ) : (
                    <User className="h-5 w-5 text-ink-soft" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-serif text-[16px] font-medium leading-tight text-ink">
                    {person.name || t('common.unknown')}
                  </p>
                  <p className="truncate text-[11px] text-ink-soft">
                    {t('sightings.count', { count: person.totalSightings })} ·{' '}
                    {t('mergeSuggestions.compare.seenRange', {
                      first: formatRelativeTime(person.firstSeenAt, '—'),
                      last: formatRelativeTime(person.lastSeenAt, '—'),
                    })}
                  </p>
                </div>
                <div className="flex shrink-0 gap-1 rounded-md border border-rule p-0.5">
                  {(['person', 'frame'] as ShotView[]).map((v) => (
                    <button
                      key={v}
                      type="button"
                      onClick={() => setView(v)}
                      className={cn(
                        'rounded-sm px-2 py-1 text-[11px] font-medium transition',
                        view === v ? 'bg-ink text-paper' : 'text-ink-soft hover:bg-paper-2'
                      )}
                    >
                      {v === 'person'
                        ? t('mergeSuggestions.compare.viewPerson')
                        : t('mergeSuggestions.compare.viewFrame')}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mt-3 flex flex-wrap items-center gap-2">
                <span className="text-[12px] text-ink-soft">
                  {t('split.selectedCount', { count: selected.size })}
                </span>
                <div className="ml-auto flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={selectSimilar}
                    disabled={selected.size === 0 || scoring}
                    title={t('split.selectSimilarHint')}
                  >
                    {scoring ? (
                      <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Sparkles className="mr-1 h-3.5 w-3.5" />
                    )}
                    {t('split.selectSimilar')}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => { setSelected(new Set()); setConfirming(false) }}
                    disabled={selected.size === 0}
                  >
                    {t('split.clear')}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Detections */}
          <div className="flex-1 overflow-y-auto px-5 py-4">
            {error && (
              <Alert variant="destructive" className="mb-4">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            {notice && (
              <Alert className="mb-4">
                <AlertDescription>{notice}</AlertDescription>
              </Alert>
            )}
            {allSelected && (
              <Alert variant="destructive" className="mb-4">
                <AlertDescription>{t('split.allSelectedWarning')}</AlertDescription>
              </Alert>
            )}

            {loading ? (
              <div className="flex justify-center py-12 text-ink-soft">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
            ) : detections.length === 0 ? (
              <p className="py-8 text-center text-[13px] text-ink-soft">{t('split.noDetections')}</p>
            ) : (
              <>
                <p className="mb-3 text-[11px] text-ink-soft">{t('split.hint')}</p>
                <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
                  {detections.map((d, idx) => {
                    const score = d.eventId ? scores.get(d.eventId) : undefined
                    return (
                      <DetectionShot
                        key={d.eventId || `d-${idx}`}
                        detection={d}
                        view={view}
                        selected={!!d.eventId && selected.has(d.eventId)}
                        onToggle={d.eventId ? () => toggle(d.eventId!) : undefined}
                        badge={score != null ? `${Math.round(score * 100)}%` : undefined}
                      />
                    )
                  })}
                </div>
                {cursor && (
                  <div className="mt-3 flex justify-center">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => load(cursor)}
                      disabled={loadingMore}
                    >
                      {loadingMore && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />}
                      {t('split.loadMore')}
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Action */}
          <div className="border-t border-rule px-5 py-3">
            {confirming ? (
              <div className="space-y-2">
                <p className="text-[12px] text-ink-soft">
                  {t('split.confirmDescription', { count: selected.size })}
                </p>
                <div className="flex items-center gap-2">
                  <Button className="flex-1" onClick={doSplit} disabled={splitting}>
                    {splitting && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
                    {t('split.confirmMove')}
                  </Button>
                  <Button variant="outline" onClick={() => setConfirming(false)} disabled={splitting}>
                    {t('common.cancel')}
                  </Button>
                </div>
              </div>
            ) : (
              <Button
                className="w-full"
                onClick={() => setConfirming(true)}
                disabled={selected.size === 0 || allSelected || loading}
              >
                <Scissors className="mr-1 h-4 w-4" />
                {t('split.moveOut', { count: selected.size })}
              </Button>
            )}
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}
